import { ArrowRight } from "lucide-react";
import type { ProductId } from "./types";
import { PRODUCT_LIST } from "./products";
import familyImage from "../../assets/visual-family-protection.jpg";
import healthImage from "../../assets/visual-health-umbrella.jpg";
import heroFamilyImage from "../../assets/visual-hero-family.jpg";
import mortgageImage from "../../assets/visual-advisor-contact.jpg";
import planningImage from "../../assets/visual-advisor-map.jpg";

const calculatorVisuals: Record<
  ProductId,
  {
    accent: "orange" | "blue" | "green";
    alt: string;
    facts: [string, string];
    image: string;
    tag: string;
  }
> = {
  ochronne: {
    accent: "orange",
    alt: "Rodzina rozmawiająca o zabezpieczeniu finansowym",
    facts: ["dla rodziny", "śmierć i dodatki"],
    image: familyImage,
    tag: "Ochrona życia"
  },
  dozycie: {
    accent: "blue",
    alt: "Planowanie długoterminowego kapitału i ochrony",
    facts: ["kapitał", "wypłata na koniec"],
    image: planningImage,
    tag: "Ochrona i oszczędzanie"
  },
  zdrowotne: {
    accent: "green",
    alt: "Symbol prywatnej opieki medycznej",
    facts: ["lekarze", "diagnostyka"],
    image: healthImage,
    tag: "Opieka medyczna"
  },
  kredyt: {
    accent: "blue",
    alt: "Rozmowa o polisie zabezpieczającej kredyt hipoteczny",
    facts: ["cesja na bank", "saldo kredytu"],
    image: mortgageImage,
    tag: "Kredyt hipoteczny"
  },
  wsparcie: {
    accent: "green",
    alt: "Bliscy objęci dożywotnią ochroną",
    facts: ["do końca życia", "dla bliskich"],
    image: heroFamilyImage,
    tag: "Dożywotnia ochrona"
  }
};

/** Rozdzielacz 5 kalkulatorów (wzorzec pru.pl). Używany na hubie oraz jako pasek
 *  „wybierz inny kalkulator” na podstronach. */
export function CalculatorHub({
  activeId,
  kicker = "Kalkulatory",
  heading = "Wybierz kalkulator",
  intro = "Wybierz rodzaj ochrony, podaj kilka parametrów i sprawdź orientacyjny budżet przed rozmową z doradcą.",
  showActive = true
}: {
  activeId?: ProductId;
  kicker?: string;
  heading?: string;
  intro?: string;
  showActive?: boolean;
}) {
  const products = showActive ? PRODUCT_LIST : PRODUCT_LIST.filter(({ id }) => id !== activeId);

  return (
    <section className="section calc-hub-section" id="kalkulatory">
      <div className="section-heading calc-hub-heading">
        {kicker && <p className="section-kicker">{kicker}</p>}
        <h2>{heading}</h2>
        <p>{intro}</p>
      </div>
      <div className="coverage-grid calc-hub-grid">
        {products.map(({ id, slug, Icon, cardTitle, cardCopy }) => {
          const visual = calculatorVisuals[id];

          return (
            <a
              className={`coverage-card link-card calc-card${id === activeId ? " is-active" : ""}`}
              href={`/${slug}/`}
              key={id}
              aria-current={id === activeId ? "page" : undefined}
              data-accent={visual.accent}
            >
              <span className="calc-card-media">
                <img src={visual.image} alt={visual.alt} loading="lazy" />
                <span className="calc-card-tag">{visual.tag}</span>
              </span>
              <span className="calc-card-body">
                <span className="calc-card-title-row">
                  <span className="icon-badge">
                    <Icon size={22} aria-hidden="true" />
                  </span>
                  <strong>{cardTitle}</strong>
                </span>
                <small>{cardCopy}</small>
                <span className="calc-card-facts" aria-label="Najważniejsze cechy">
                  {visual.facts.map((fact) => (
                    <span key={fact}>{fact}</span>
                  ))}
                </span>
                <span className="calc-card-action">
                  Otwórz kalkulator
                  <ArrowRight size={18} aria-hidden="true" />
                </span>
              </span>
            </a>
          );
        })}
      </div>
    </section>
  );
}
